import { createStore } from 'effector'
import dayjs from 'dayjs'
import connectLocalStorage from 'effector-localstorage'

import {
  $getLoginRefreshFx,
  $getLogoutFx,
  $loginClear,
  $loginSendCodeClear,
  $postLogin,
  $postLoginSendCode,
  $postLoginType,
  $postRecovery,
  $postRecoverySendCode,
  $postRecoveryValidate,
  $postRegister,
  $postSendCode,
  $postValidate,
  $recoverySendCodeClear,
  $setAuthToken,
  $setCode,
  $setEmail,
  $setPhone,
} from './event'
import { $navigate } from '../History/store'
import { $activeLanguage } from '../Languages/store'

import {
  PostLoginRes,
  PostLoginSendCodeRes,
  PostLoginTypeRes,
  PostRecoverySendCodeRes,
  PostRegRes,
  PostRegSendCodeRes,
} from '../../services/api/Auth'

import { AuthParams, AuthType, Common, RecoveryType, Routes } from '../../shared/enum'

import { Errors } from '../types'
import { APIError } from '../../core/api/Result'

// AuthToken
const authTokenLocalStorage = connectLocalStorage(Common.AUTHTOKEN)
export const $authToken = createStore(authTokenLocalStorage.init(''))
  .on($setAuthToken, (_, authToken) => authToken)
  .on($getLogoutFx.finally, () => {
    setTimeout(() => window.location.reload(), 0)
    return ''
  })
  .on($postRegister.done, (_, { result }) => result.data.data.token)
  .on($postLogin.done, (_, { result }) => result.data.data.token)
  .on($getLoginRefreshFx.done, (_, { result }) => result.data.data.token)
  .on($getLoginRefreshFx.fail, (_, { error }) => {
    error.status === 401 && $getLogoutFx()
    return ''
  })
$authToken.watch(authTokenLocalStorage)

export const $refreshLoading = createStore(false)
  .on($getLoginRefreshFx.pending, (_, pending) => pending)

const inviteIdLocalStorage = connectLocalStorage(AuthParams.INVITE_ID)
export const $inviteId = createStore(inviteIdLocalStorage.init(null))

export const $phone = createStore<string>('')
  .on($setPhone, (_, phone) => phone)

export const $email = createStore<string>('')
  .on($setEmail, (_, phone) => phone)

export const $candidateId = createStore<string>('')
  .on($postValidate.done, (_, { result, params }) => {
    if (params.context !== 'code') {
      return result.data.data.candidateId
    }
  })

export const $code = createStore<string>('')
  .on($setCode, (_, code) => code)

// Register - Validate
// Checks for registration, and redirects to its own page
export const $validate = createStore<APIError | null>(null)
  .on($postValidate.done, (_, { params }) => {
    if (params.context === 'code') {
      const candidateId = $candidateId.getState()
      $postRegister({ hash: candidateId })
    }
    const navigate = $navigate.getState()
    const { code } = $activeLanguage.getState()

    if (params.context === 'phone') {
      navigate(`/${code}/${Routes.AUTH}/${Routes.REGISTER}/${Routes.PASSWORD}`)
    }
    if (params.context === 'email') {
      $postSendCode({ email: params.email, instance: AuthType.EMAIL })
    }
  })
  .on($postValidate.fail, (_, { error, params }) => {
    if (
      params.context !== 'code' &&
      (error.data.errors?.phone?.messages.includes('validation.phone.exists') ||
        error.data.message.includes('exists'))
    ) {
      const phone = $phone.getState()
      const email = $email.getState()
      $postLoginType({ phone, email })
      return null
    }
    return error.data
  })
  .on($postLoginType.done, (_, { result }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { password } = result.data.data
    const navigate = $navigate.getState()
    const { code } = $activeLanguage.getState()

    // if (password) {
    //   navigate(`/${code}/${Routes.AUTH}/${Routes.LOGIN}/${Routes.PASSWORD}`)
    // } else {
    //   navigate(`/${code}/${Routes.AUTH}/${Routes.LOGIN}/${Routes.SELECT}`)
    // }
    navigate(`/${code}/${Routes.AUTH}/${Routes.LOGIN}/${Routes.SELECT}`)
  })
export const $isValidatePending = createStore<boolean>(false)
  .on($postValidate.pending, (_, pending) => pending)
  .on($postLoginType.pending, (_, pending) => pending)

export const $postLoginTypeStore = createStore<Partial<PostLoginTypeRes>>({})
  .on($postLoginType.done, (_, { result }) => result.data.data)

// Register
export const $register = createStore<Partial<PostRegRes>>({})
  .on($postRegister.done, (_, { result }) => result.data.data)
export const $registerPending = createStore(false)
  .on($postRegister.pending, (_, pending) => pending)
export const $registerError = createStore<Errors | null>(null)
  .on($postRegister.fail, (_, { error }) => error.data.errors)
  .on($setCode, () => null)

// Register - Send-Code
export const $registerSendCode = createStore<Partial<PostRegSendCodeRes>>(false)
  .on($postSendCode.done, (_, { result, params }) => {
    if (params.email && params.instance === AuthType.EMAIL) {
      const navigate = $navigate.getState()
      const { code } = $activeLanguage.getState()
      navigate(`/${code}/${Routes.AUTH}/${Routes.REGISTER}/${Routes.EMAIL}`)
    }
    return result.data.data
  })
  .on($loginSendCodeClear, () => false)
export const $registerSendCodePending = createStore<boolean>(false)
  .on($postSendCode.pending, (_, pending) => pending)

export const $registerSendCodeError = createStore<Errors | null>(null)
  .on($postSendCode.fail, (_, { error }) => {
    return error.data.errors
  })
  .on($loginSendCodeClear, () => null)
  .on($postSendCode.done, () => null)
  .on($setCode, () => null)
export const $registerSendCodeTime = createStore<string | null>(null)
  .on($postSendCode.finally, () => {
    return dayjs().add(60, 's').toISOString()
  })

// Login - Send-Code
export const $loginSendCode = createStore<Partial<PostLoginSendCodeRes>>({})
  .on($postLoginSendCode.done, (_, { result }) => {
    const navigate = $navigate.getState()
    const { code } = $activeLanguage.getState()
    const instance = result.data.data.sendCodeToInstance

    if (instance === AuthType.PASSWORD || instance === AuthType.ANY) {
      navigate(`/${code}/${Routes.AUTH}/${Routes.LOGIN}/${Routes.PASSWORD}`)
    }
    if ([AuthType.TELEGRAM, AuthType.SMS, AuthType.WHATSAPP, AuthType.EMAIL].includes(instance)) {
      navigate(`/${code}/${Routes.AUTH}/${Routes.LOGIN}/${Routes.CODE}`)
    }
    return result.data.data
  })
  .on($loginSendCodeClear, () => ({}))
export const $loginSendCodeError = createStore<string>('')
  .on($postLoginSendCode.fail, (_, { error }) => {
    return error.data.message
  })
  .on($loginSendCodeClear, () => '')
export const $loginSendCodeTime = createStore<string | null>(null)
  .on($postLoginSendCode.done, () => {
    return dayjs().add(60, 's').toISOString()
  })
export const $loginSendPending = createStore<boolean>(false)
  .on($postLoginSendCode.pending, (_, pending) => pending)
  .on($postLoginType.pending, (_, pending) => pending)

// Login
export const $login = createStore<Partial<PostLoginRes>>({})
  .on($postLogin.done, (_, { result }) => {
    return result.data.data
  })
  .on($loginClear, () => ({}))
export const $loginError = createStore<boolean>(false)
  .on($postLogin.fail, () => true)
  .on($loginClear, () => false)
export const $loginLoading = createStore<boolean>(false)
  .on($postLogin.pending, (_, pending) => pending)

// Recovery - Send-code
export const $recoverySendCode = createStore<Partial<PostRecoverySendCodeRes>>({})
  .on($postRecoverySendCode.done, (_, { result }) => {
    const { instance } = result.data.data
    const navigate = $navigate.getState()
    const { code } = $activeLanguage.getState()
    if (instance === RecoveryType.PASSPORT) {
      navigate(`/${code}/${Routes.AUTH}/${Routes.RECOVERY}/${Routes.PASSPORT}`)
    }
    if (instance === RecoveryType.PASSWORD) {
      navigate(`${code}/${Routes.AUTH}/${Routes.RECOVERY}/${Routes.PASSWORD}`)
    }
    return result.data.data
  })
  .on($recoverySendCodeClear, () => ({}))
export const $recoverySendCodeTime = createStore<string | null>(null)
  .on($postLoginSendCode.done, () => {
    return dayjs().toISOString()
  })
export const $recoverySendCodeError = createStore<string | null>(null)
  .on($postRecoverySendCode.fail, (_, { error }) => {
    return error.data.message
  })
  .on($recoverySendCodeClear, () => null)
export const $recoverySendCodePending = createStore<boolean>(false)
  .on($postRecoverySendCode.pending, (_, pending) => pending)

// Recovery
export const $isRecovery = createStore<boolean>(false)
  .on($postRecovery.done, (_, { result }) => {
    $setAuthToken(result.data.data.token)
    return true
  })
export const $recoveryLoading = createStore<boolean>(false)
  .on($postRecovery.pending, (_, pending) => pending)
export const $recoveryError = createStore<string | null | boolean>(null)
  .on($postRecovery.done, () => false)
  .on($postRecovery.fail, (_, { error }) => {
    return error.data.message
  })
  .on($recoverySendCodeClear, () => null)
export const $recoveryPending = createStore<boolean>(false)
  .on($postRecoverySendCode.pending, (_, pending) => pending)

export const $recoveryValidate = createStore<boolean | null>(null)
  .on($postRecoveryValidate.done, () => false)
export const $recoveryValidateError = createStore<string | null>(null)
  .on($postRecoveryValidate.fail, (_, { error }) => error.data.message)
export const $recoveryValidatePending = createStore<boolean>(false)
  .on($postRecoveryValidate.pending, (_, pending) => pending)
